/**
 * React Query hook for a single PandaScore match (with games / per-map results):
 *   GET /api/PandaScore/matches/{idOrSlug}
 *
 * Hand-written to match the orval/react-query + customInstance convention
 * (the query function resolves to the customFetch wrapper, so consumers read
 * `query.data?.data`). Replace with the generated hook after deploy + regen.
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { PandaScoreMatch } from '@/api/teams/teams.types';
import { env } from '@/config/env';
import customFetch from '@/lib/customInstance';

const BASE = `${env.NEXT_PUBLIC_API_URL}/api/PandaScore/matches`;

type FetchWrapper<T> = { status: number; data: T; headers: Headers };

export const getMatch = (idOrSlug: string, signal?: AbortSignal) =>
  customFetch<PandaScoreMatch>({
    url: `${BASE}/${encodeURIComponent(idOrSlug)}`,
    method: 'GET',
    signal,
  });

export const matchKeys = {
  detail: (idOrSlug: string) => ['matches', idOrSlug, 'detail'] as const,
};

export const useGetMatch = (
  idOrSlug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        FetchWrapper<PandaScoreMatch>,
        unknown,
        FetchWrapper<PandaScoreMatch>
      >
    >;
  }
) =>
  useQuery({
    queryKey: matchKeys.detail(idOrSlug),
    queryFn: ({ signal }) => getMatch(idOrSlug, signal),
    enabled: Boolean(idOrSlug),
    ...options?.query,
  });
